import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Button } from "react-bootstrap";
import { getGreekCurrencyFormat, getDOYOptions } from "../../../../_helpers/commonFunctions";

const CrossableNumber = ({ number, isCrossed }) => (
    <div style={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%"
    }}>
        <span>{number}</span>
        {isCrossed && (
            <span style={{
                position: "absolute",
                fontWeight: "normal",
                fontSize: "1.5rem",
                lineHeight: 1,
                color: "rgba(0, 0, 0, 0.45)"
            }}>X</span>
        )}
    </div>
);

const SingleCell = ({ value }) => {
    const num = Number(value);
    const hasValue = !isNaN(num) && num !== 0;
    const formatted = hasValue ? getGreekCurrencyFormat(num) : "";

    return (
        <div className="w-100 h-100 d-flex align-items-center justify-content-center px-1" style={{ minHeight: "22px" }}>
            <span className="text-truncate w-100 text-center" style={{ fontSize: "0.65rem", fontWeight: "600", letterSpacing: "-0.3px" }}>
                {formatted}
            </span>
        </div>
    );
};

const AccountingVatDeclarationView = ({ tabKey, declarationData }) => {
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const companyDetails = useSelector((state) => state.COMPANY_DATA.companyDetails);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragScrollLeft, setDragScrollLeft] = useState(0);

    const reduxDeclaration = useSelector((state) => {
        if (!tabKey) return null;
        return state.ACCOUNTING_DATA?.selectedSubmittedVatDeclarations?.[tabKey] ?? null;
    });

    const decl = declarationData || reduxDeclaration || {};
    const fields = decl.fields || {};

    const getFieldValue = (code) => {
        const val = fields[String(code)] ?? fields[String(code).padStart(3, '0')];
        return Number(val) || 0;
    };

    const is507Crossed = (idx) => {
        return Boolean(
            fields[`507C${idx}`] === 1 ||
            fields[`507C${idx}`] === true ||
            fields[`507C${idx}`] === "1" ||
            fields[`f507C${idx}`] === 1 ||
            Number(fields['507']) === idx
        );
    };

    const periodMonth = decl.month !== undefined && decl.month !== null ? Number(decl.month) : null;
    const submissionYear = decl.year || (decl.submissionDate ? decl.submissionDate.split('/')[2] : "");
    const declType = decl.declarationType || "INITIAL";
    const declarationNumber = fields['001'] || "";

    const formStyle = {
        fontFamily: "'Arial', sans-serif",
        fontSize: "0.7rem",
        color: "#000",
        border: "2px solid #000",
        boxSizing: "border-box"
    };
    const grayBg = { backgroundColor: "#e2edf8" };
    const borderDark = "1px solid #000";
    const doyOptions = getDOYOptions();
    let doyLabel = companyDetails?.doy || "";
    let doyCode = "";
    if (companyDetails?.doy) {
        const matchedDoy = doyOptions.find(opt =>
            String(opt.value) === String(companyDetails.doy) ||
            String(opt.label) === String(companyDetails.doy)
        );
        if (matchedDoy) {
            doyLabel = matchedDoy.label;
            doyCode = String(matchedDoy.value).padStart(4, '0');
        }
    }

    return (
        <div className="w-100 d-flex flex-column" style={{ minHeight: "100%" }}>
            <style>{`
                .f2-layout table td.p-0 { height: 1px; }
                .f2-declaration-checkbox {
                    -webkit-appearance: none;
                    appearance: none;
                    margin: 0;
                    width: 14px;
                    height: 14px;
                    border: 1px solid #777;
                    border-radius: 2px;
                    background-color: #fff;
                    display: inline-grid;
                    place-content: center;
                    outline: none;
                }
                .f2-declaration-checkbox:checked {
                    background-color: #76AEB7;
                    border-color: #76AEB7;
                }
                .f2-declaration-checkbox:checked::before {
                    content: "";
                    width: 4px;
                    height: 8px;
                    border: solid #ffffff;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                    margin-bottom: 2px;
                }
            `}</style>

            <div
                className="w-100 py-3"
                style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "auto"
                }}
                onMouseDown={(e) => {
                    if (zoomLevel <= 1) return;
                    setIsDragging(true);
                    setDragStartX(e.clientX);
                    setDragScrollLeft(e.currentTarget.scrollLeft);
                }}
                onMouseLeave={() => setIsDragging(false)}
                onMouseUp={() => setIsDragging(false)}
                onMouseMove={(e) => {
                    if (!isDragging) return;
                    e.preventDefault();
                    const walk = (e.clientX - dragStartX) * 1.5;
                    e.currentTarget.scrollLeft = dragScrollLeft - walk;
                }}
            >
                <div style={{ width: "1200px", margin: "0 auto", userSelect: isDragging ? "none" : "auto" }}>
                    <div className="bg-white f2-layout" style={{ ...formStyle, width: "1200px", zoom: zoomLevel }}>
                        <div className="position-relative w-100 bg-white"
                             style={{
                                 borderBottom: borderDark,
                                 padding: "10px",
                                 paddingBottom: "15px",
                                 minHeight: "260px"
                             }}>
                            <div className="position-absolute" style={{
                                top: 0, left: 0, width: "220px", height: "220px",
                                backgroundColor: "#009FE3",
                                clipPath: "polygon(0 0, 100% 0, 0 100%)",
                                zIndex: 1,
                                pointerEvents: "none"
                            }}>
                                <div style={{
                                    position: "absolute",
                                    top: "15px",
                                    left: "25px",
                                    color: "#fff",
                                    fontSize: "3.5rem",
                                    fontWeight: "bold",
                                    lineHeight: 1,
                                    fontFamily: "Arial, sans-serif"
                                }}>
                                    Φ<span style={{
                                    fontSize: "2.2rem",
                                    position: "relative",
                                    top: "4px",
                                    marginLeft: "2px"
                                }}>2</span>
                                </div>
                                <div style={{
                                    position: "absolute",
                                    top: "95px",
                                    left: "95px",
                                    width: "300px",
                                    transform: "translate(-50%, -50%) rotate(-45deg)",
                                    textAlign: "center"
                                }}>
                                    <div style={{
                                        width: "100%",
                                        height: "1px",
                                        backgroundColor: "#fff",
                                        opacity: 0.9,
                                        marginBottom: "4px"
                                    }}></div>
                                    <div style={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        fontSize: "0.75rem",
                                        letterSpacing: "16px",
                                        paddingLeft: "16px"
                                    }}>
                                        TAXIS
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between">
                                <div className="d-flex flex-column"
                                     style={{
                                         width: "55%",
                                         paddingTop: "5px",
                                         paddingLeft: "160px",
                                         zIndex: 2
                                     }}>
                                    <div className="text-center mb-4">
                                        <h4 className="font-weight-bold mb-0"
                                            style={{fontSize: "1.6rem", color: "#000"}}>ΔΗΛΩΣΗ Φ.Π.Α.</h4>
                                        <div style={{
                                            fontSize: "0.75rem",
                                            color: "#005a9c",
                                            fontWeight: "bold",
                                            letterSpacing: "-0.5px"
                                        }}>Υποβάλλεται σε δύο (2) αντίτυπα (έντυπη υποβολή).
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center position-relative mb-1"
                                         style={{width: "95%"}}>
                                        <div className="d-flex w-100" style={{border: borderDark}}>
                                            <div
                                                className="d-flex align-items-end p-1 border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "65%",
                                                    fontSize: "0.7rem"
                                                }}>
                                                <span className="font-weight-bold mr-1">ΑΡΜΟΔΙΑ Δ.Ο.Υ.</span>
                                                <span className="flex-fill" style={{
                                                    borderBottom: "1px dotted #000",
                                                    height: "12px",
                                                    overflow: "hidden",
                                                    whiteSpace: "nowrap"
                                                }}>{doyLabel}</span>
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{borderColor: "#000", width: "10%", ...grayBg}}>
                                                003
                                            </div>
                                            <div className="position-relative" style={{width: "25%"}}>
                                                <div
                                                    className="d-flex w-100 h-100 align-items-center justify-content-around font-weight-bold pb-1"
                                                    style={{fontSize: "0.75rem"}}>
                                                    {doyCode ? doyCode.split('').map((char, index) => <span
                                                        key={index}>{char}</span>) : ""}
                                                </div>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(4)].map((_, i) => (
                                                        <div key={i} className="flex-fill"
                                                             style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <span className="position-absolute font-weight-bold"
                                              style={{right: "-12px", top: "2px"}}>*</span>
                                    </div>
                                    <div className="d-flex flex-column mb-3"
                                         style={{width: "85%", border: borderDark, marginLeft: "10%"}}>
                                        <div className="d-flex" style={{borderBottom: borderDark}}>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{borderColor: "#000", width: "12%", ...grayBg}}>
                                                005
                                            </div>
                                            <div className="flex-fill text-center p-1"
                                                 style={{fontSize: "0.7rem", color: "#005a9c"}}>
                                                ΗΜΕΡΟΛΟΓΙΑΚΗ ΠΕΡΙΟΔΟΣ
                                            </div>
                                        </div>
                                        <div className="d-flex" style={{height: "22px"}}>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "12%",
                                                    fontSize: "0.7rem",
                                                    color: "#005a9c"
                                                }}>από
                                            </div>
                                            <div className="position-relative border-right border-dark"
                                                 style={{borderColor: "#000", width: "38%"}}>
                                                <div
                                                    className="d-flex align-items-center justify-content-center w-100 h-100 font-weight-bold"
                                                    style={{
                                                        letterSpacing: "2px",
                                                        fontSize: "0.75rem"
                                                    }}>{decl.periodStart || ""}</div>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(4)].map((_, i) => (
                                                        <div key={i} className="flex-fill"
                                                             style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "12%",
                                                    fontSize: "0.7rem",
                                                    color: "#005a9c"
                                                }}>έως
                                            </div>
                                            <div className="position-relative" style={{width: "38%"}}>
                                                <div
                                                    className="d-flex align-items-center justify-content-center w-100 h-100 font-weight-bold"
                                                    style={{
                                                        letterSpacing: "2px",
                                                        fontSize: "0.75rem"
                                                    }}>{decl.periodEnd || ""}</div>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(4)].map((_, i) => (
                                                        <div key={i} className="flex-fill"
                                                             style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column pt-1"
                                         style={{width: "90%", border: borderDark, marginLeft: "5%"}}>
                                        <div className="text-center p-1"
                                             style={{
                                                 borderBottom: borderDark,
                                                 color: "#005a9c",
                                                 fontSize: "0.8rem",
                                                 letterSpacing: "1px"
                                             }}>
                                            ΕΙΔΟΣ &nbsp;&nbsp;ΔΗΛΩΣΗΣ
                                        </div>
                                        <div className="d-flex w-100" style={{height: "25px"}}>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{borderColor: "#000", width: "11%", ...grayBg}}>007
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark text-center"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "15%",
                                                    fontSize: "0.55rem",
                                                    lineHeight: 1,
                                                    color: "#005a9c"
                                                }}>
                                                ΤΡΟΠΟ-<br/>ΠΟΙΗΤΙΚΗ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark"
                                                style={{borderColor: "#000", width: "7.33%"}}>
                                                <input type="checkbox" className="f2-declaration-checkbox"
                                                       checked={declType === "AMENDING" || fields['007'] === 1} disabled={true}
                                                />
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{borderColor: "#000", width: "11%", ...grayBg}}>008
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark text-center"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "15%",
                                                    fontSize: "0.55rem",
                                                    lineHeight: 1,
                                                    color: "#005a9c"
                                                }}>
                                                ΜΕ ΕΠΙ-<br/>ΦΥΛΑΞΗ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark"
                                                style={{borderColor: "#000", width: "7.33%"}}>
                                                <input type="checkbox" className="f2-declaration-checkbox"
                                                       checked={declType === "WITH_RESERVATION" || fields['008'] === 1} disabled={true}
                                                />
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{borderColor: "#000", width: "11%", ...grayBg}}>009
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-right border-dark text-center"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "15%",
                                                    fontSize: "0.55rem",
                                                    lineHeight: 1,
                                                    color: "#005a9c"
                                                }}>
                                                ΕΚΤΑΚΤΗ<br/>ΔΗΛΩΣΗ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "7.33%",
                                                    borderRight: "none"
                                                }}>
                                                <input type="checkbox" className="f2-declaration-checkbox"
                                                       checked={declType === "EXTRAORDINARY" || fields['009'] === 1} disabled={true}
                                                />
                                            </div>
                                            <div className="flex-fill"></div>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex flex-column align-items-end"
                                     style={{width: "45%", paddingTop: "10px", zIndex: 2}}>
                                    <div className="position-relative mb-4"
                                         style={{width: "92%", border: borderDark}}>
                                        <div className="d-flex w-100"
                                             style={{borderBottom: borderDark, height: "26px"}}>
                                            <div
                                                className="d-flex align-items-center font-weight-bold border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "28%",
                                                    fontSize: "0.65rem",
                                                    lineHeight: "1.1",
                                                    paddingLeft: "4px",
                                                    color: "#005a9c"
                                                }}>
                                                ΑΡΙΘΜΟΣ<br/>ΔΗΛΩΣΗΣ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "15%", ...grayBg,
                                                    fontSize: "0.75rem"
                                                }}>
                                                001
                                            </div>
                                            <div
                                                className="d-flex flex-column position-relative border-right border-dark"
                                                style={{borderColor: "#000", width: "42%"}}>
                                                <div className="d-flex w-100 h-100 align-items-center justify-content-center font-weight-bold pb-1"
                                                     style={{fontSize: "0.75rem", letterSpacing: "1px"}}>
                                                    {declarationNumber}
                                                </div>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(6)].map((_, i) => (
                                                        <div key={i} className="flex-fill"
                                                             style={{borderRight: i === 5 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="d-flex flex-column position-relative"
                                                 style={{width: "15%"}}>
                                                <div className="text-center w-100"
                                                     style={{
                                                         fontSize: "0.6rem",
                                                         marginTop: "1px",
                                                         color: "#005a9c"
                                                     }}>ΕΤΟΣ
                                                </div>
                                                <div
                                                    className="d-flex w-100 h-100 align-items-center justify-content-center font-weight-bold pb-1"
                                                    style={{fontSize: "0.75rem", letterSpacing: "1px"}}>
                                                    {submissionYear}
                                                </div>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(2)].map((_, i) => (
                                                        <div key={i} className="flex-fill"
                                                             style={{borderRight: i === 1 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex w-100" style={{height: "26px"}}>
                                            <div
                                                className="d-flex align-items-center font-weight-bold border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "28%",
                                                    fontSize: "0.65rem",
                                                    lineHeight: "1.1",
                                                    paddingLeft: "4px",
                                                    letterSpacing: "-0.3px",
                                                    color: "#005a9c"
                                                }}>
                                                ΗΜΕΡΟΜΗΝΙΑ<br/>ΥΠΟΒΟΛΗΣ ΔΗΛΩΣΗΣ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                style={{
                                                    borderColor: "#000",
                                                    width: "15%", ...grayBg,
                                                    fontSize: "0.75rem"
                                                }}>
                                                002
                                            </div>
                                            <div
                                                className="d-flex border-right border-dark position-relative"
                                                style={{borderColor: "#000", width: "42%"}}>
                                                <div
                                                    className="d-flex w-100 h-100 align-items-center justify-content-center font-weight-bold pb-1"
                                                    style={{fontSize: "0.75rem", letterSpacing: "1px"}}>
                                                    {decl.submissionDate || ""}
                                                </div>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(10)].map((_, i) => (
                                                        <div key={i} className="flex-fill"
                                                             style={{borderRight: i === 9 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="position-relative" style={{width: "15%"}}>
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    <div className="flex-fill"
                                                         style={{borderRight: borderDark}}></div>
                                                    <div className="flex-fill"></div>
                                                </div>
                                            </div>
                                        </div>
                                        <span className="position-absolute font-weight-bold"
                                              style={{right: "-12px", top: "5px", fontSize: "0.75rem"}}>*</span>
                                        <span className="position-absolute font-weight-bold"
                                              style={{right: "-12px", bottom: "5px", fontSize: "0.75rem"}}>*</span>
                                    </div>
                                    <div className="d-flex flex-column justify-content-end"
                                         style={{width: "100%"}}>
                                        <div className="d-flex justify-content-end" style={{
                                            paddingRight: "0px",
                                            marginBottom: "-1px",
                                            zIndex: 1,
                                            position: "relative"
                                        }}>
                                            <div className="d-flex" style={{
                                                border: borderDark,
                                                backgroundColor: "#fff",
                                                height: "22px"
                                            }}>
                                                <div
                                                    className="d-flex align-items-center px-2 font-weight-bold border-right border-dark"
                                                    style={{borderColor: "#000", fontSize: "0.65rem"}}>ΕΤΟΣ
                                                </div>
                                                <div
                                                    className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                    style={{
                                                        borderColor: "#000", ...grayBg,
                                                        width: "35px",
                                                        fontSize: "0.75rem"
                                                    }}>004
                                                </div>
                                                <div
                                                    className="position-relative d-flex align-items-center justify-content-center font-weight-bold"
                                                    style={{width: "55px", fontSize: "0.65rem"}}>
                                                    {submissionYear}
                                                    <div className="d-flex w-100 position-absolute"
                                                         style={{bottom: 0, height: "4px"}}>
                                                        {[...Array(4)].map((_, i) => (
                                                            <div key={i} className="flex-fill"
                                                                 style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white"
                                             style={{border: borderDark, position: "relative", zIndex: 0}}>
                                            <div className="text-center font-weight-bold py-1"
                                                 style={{
                                                     borderBottom: borderDark,
                                                     color: "#005a9c",
                                                     fontSize: "0.75rem"
                                                 }}>
                                                ΦΟΡΟΛΟΓΙΚΗ ΠΕΡΙΟΔΟΣ (Διαγραμμίστε με X)
                                            </div>
                                            <table className="w-100 text-center m-0"
                                                   style={{
                                                       tableLayout: "fixed",
                                                       borderCollapse: "collapse"
                                                   }}>
                                                <colgroup>
                                                    <col style={{width: "22%"}}/>
                                                    <col style={{width: "8%"}}/>
                                                    {[...Array(12)].map((_, i) => (
                                                        <col key={`col-${i}`} style={{width: "5.83%"}}/>
                                                    ))}
                                                </colgroup>
                                                <tbody>
                                                <tr>
                                                    <td className="align-middle p-0" style={{
                                                        fontSize: "0.65rem",
                                                        borderBottom: borderDark,
                                                        borderRight: borderDark
                                                    }}>ΜΗΝΑΣ
                                                    </td>
                                                    <td rowSpan={4} className="align-middle p-0" style={{
                                                        ...grayBg,
                                                        borderRight: borderDark,
                                                        borderLeft: borderDark
                                                    }}>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold w-100 h-100"
                                                            style={{fontSize: "0.75rem"}}>006
                                                        </div>
                                                    </td>
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                                                        <td key={`m-${m}`} className="p-0 align-middle"
                                                            style={{
                                                                height: "22px",
                                                                borderBottom: borderDark,
                                                                borderRight: m === 12 ? "none" : borderDark
                                                            }}>
                                                            <CrossableNumber number={m}
                                                                             isCrossed={periodMonth === m}/>
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr>
                                                    <td className="align-middle p-0" style={{
                                                        fontSize: "0.65rem",
                                                        borderBottom: borderDark,
                                                        borderRight: borderDark
                                                    }}>ΤΡΙΜΗΝΟ
                                                    </td>
                                                    {[1, 2, 3, 4].map(m => (
                                                        <td key={`q-${m}`} className="p-0 align-middle"
                                                            style={{
                                                                height: "22px",
                                                                borderBottom: borderDark,
                                                                borderRight: m === 4 ? borderDark : borderDark
                                                            }}>
                                                            <CrossableNumber number={m}
                                                                             isCrossed={false}/>
                                                        </td>
                                                    ))}
                                                    <td colSpan={8} style={{border: "none"}}></td>
                                                </tr>
                                                <tr>
                                                    <td className="align-middle p-0" style={{
                                                        fontSize: "0.65rem",
                                                        borderBottom: borderDark,
                                                        borderRight: borderDark
                                                    }}>ΕΞΑΜΗΝΟ
                                                    </td>
                                                    {[1, 2].map(m => (
                                                        <td key={`s-${m}`} className="p-0 align-middle"
                                                            style={{
                                                                height: "22px",
                                                                borderBottom: borderDark,
                                                                borderRight: m === 2 ? borderDark : borderDark
                                                            }}>
                                                            <CrossableNumber number={m}
                                                                             isCrossed={false}/>
                                                        </td>
                                                    ))}
                                                    <td colSpan={10} style={{border: "none"}}></td>
                                                </tr>
                                                <tr>
                                                    <td className="align-middle p-0" style={{
                                                        fontSize: "0.65rem",
                                                        borderBottom: "none",
                                                        borderRight: borderDark
                                                    }}>ΕΤΗΣΙΑ
                                                    </td>
                                                    <td className="p-0 align-middle" style={{
                                                        height: "22px",
                                                        borderBottom: "none",
                                                        borderRight: borderDark
                                                    }}>
                                                        <CrossableNumber number={1}
                                                                         isCrossed={false}/>
                                                    </td>
                                                    <td colSpan={11} style={{border: "none"}}></td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white" style={{border: borderDark}}>
                            <div className="p-1 d-flex align-items-center"
                                 style={{borderBottom: borderDark, fontSize: "0.75rem", color: "#000"}}>
                                <span className="font-weight-bold">Α. ΠΙΝΑΚΑΣ ΜΕ ΤΑ ΣΤΟΙΧΕΙΑ ΤΟΥ ΥΠΟΚΕΙΜΕΝΟΥ ΣΤΟ ΦΟΡΟ Ή ΛΗΠΤΗ</span>
                            </div>
                            <div className="d-flex">
                                <div style={{width: "2%", borderRight: borderDark}}></div>
                                <div className="d-flex flex-column flex-fill">
                                    <div className="d-flex w-100"
                                         style={{borderBottom: borderDark, height: "30px"}}>
                                        <div
                                            className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                            style={{
                                                width: "5%", ...grayBg,
                                                borderRight: borderDark,
                                                fontSize: "0.7rem"
                                            }}>
                                            101
                                        </div>
                                        <div className="flex-fill d-flex flex-column px-2"
                                             style={{position: "relative"}}>
                                        <span style={{
                                            fontSize: "0.65rem",
                                            color: "#005a9c",
                                            marginTop: "1px"
                                        }}>ΕΠΩΝΥΜΟ Ή ΕΠΩΝΥΜΙΑ</span>
                                            <span className="font-weight-bold" style={{
                                                fontSize: "0.75rem",
                                                marginTop: "-3px"
                                            }}>{company?.name || ""}</span>
                                        </div>
                                    </div>
                                    <div className="d-flex w-100" style={{height: "30px"}}>
                                        <div
                                            className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                            style={{
                                                width: "5%", ...grayBg,
                                                borderRight: borderDark,
                                                fontSize: "0.7rem"
                                            }}>
                                            102
                                        </div>
                                        <div className="d-flex flex-column px-2"
                                             style={{width: "42%", borderRight: borderDark}}>
                                            <span style={{
                                                fontSize: "0.65rem",
                                                color: "#005a9c",
                                                marginTop: "1px"
                                            }}>ΟΝΟΜΑ</span>
                                        </div>

                                        <div
                                            className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                            style={{
                                                width: "5%", ...grayBg,
                                                borderRight: borderDark,
                                                fontSize: "0.7rem"
                                            }}>
                                            103
                                        </div>
                                        <div className="d-flex flex-column px-2"
                                             style={{width: "23%", borderRight: borderDark}}>
                                            <span style={{
                                                fontSize: "0.65rem",
                                                color: "#005a9c",
                                                marginTop: "1px"
                                            }}>ΟΝΟΜΑ ΠΑΤΕΡΑ</span>
                                        </div>

                                        <div
                                            className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                            style={{
                                                width: "5%", ...grayBg,
                                                borderRight: borderDark,
                                                fontSize: "0.7rem"
                                            }}>
                                            104
                                        </div>
                                        <div className="flex-fill d-flex align-items-center position-relative px-2">
                                            <span className="align-self-start" style={{
                                                fontSize: "0.65rem",
                                                color: "#005a9c",
                                                marginTop: "1px",
                                                marginRight: "10px"
                                            }}>Α.Φ.Μ.</span>
                                            <div className="flex-fill position-relative h-100">
                                                <div className="d-flex w-100 position-absolute"
                                                     style={{bottom: 0, height: "4px"}}>
                                                    {[...Array(9)].map((_, i) => (
                                                        <div key={`afm-tick-${i}`} className="flex-fill"
                                                             style={{borderRight: i === 8 ? "none" : borderDark}}></div>
                                                    ))}
                                                </div>
                                                <div
                                                    className="d-flex justify-content-around w-100 position-absolute"
                                                    style={{
                                                        bottom: "2px",
                                                        fontWeight: "bold",
                                                        fontSize: "0.7rem",
                                                        letterSpacing: "2px"
                                                    }}>
                                                    {(decl.companyVatNumber || company?.vatNumber) ? (decl.companyVatNumber || company.vatNumber).split('').map((digit, index) => (
                                                        <span key={`digit-${index}`}>{digit}</span>
                                                    )) : ""}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white" style={{border: borderDark}}>
                            <div className="p-1 d-flex align-items-center"
                                 style={{borderBottom: borderDark, fontSize: "0.75rem", color: "#000"}}>
                                <span className="font-weight-bold mr-2">Β. ΠΙΝΑΚΑΣ ΕΚΡΟΩΝ - ΕΙΣΡΟΩΝ</span>
                                <span className="font-weight-normal">μετά την αφαίρεση (κατά συντελεστή) των επιστροφών - εκπτώσεων.</span>
                            </div>

                            <Row className="no-gutters">
                                <Col lg={6} className="border-right border-dark"
                                     style={{borderColor: "#000", borderRightWidth: "2px"}}>
                                    <table className="w-100 text-center m-0"
                                           style={{tableLayout: "fixed", borderCollapse: "collapse"}}>
                                        <colgroup>
                                            <col style={{width: "4%"}}/>
                                            <col style={{width: "24%"}}/>
                                            <col style={{width: "7%"}}/>
                                            <col style={{width: "26%"}}/>
                                            <col style={{width: "6%"}}/>
                                            <col style={{width: "7%"}}/>
                                            <col style={{width: "26%"}}/>
                                        </colgroup>
                                        <thead>
                                        <tr className="align-middle">
                                            <th rowSpan={2} className="p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000"
                                            }}>α
                                            </th>
                                            <th colSpan={3} className="p-1" style={{
                                                fontSize: "0.65rem",
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000"
                                            }}>
                                                ΕΚΡΟΕΣ φορολογητέες (πωλήσεις αγαθών,παροχή<br/>υπηρεσιών
                                                κλπ),
                                                ΕΝΔΟΚ.
                                                ΑΠΟΚΤ. & ΠΡΑΞ. ΛΗΠΤΗ
                                            </th>
                                            <th rowSpan={2} className="p-1" style={{
                                                fontSize: "0.65rem",
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000"
                                            }}>Συντ.<br/>ΦΠΑ<br/>%
                                            </th>
                                            <th colSpan={2} className="p-1"
                                                style={{fontSize: "0.6rem", borderBottom: "1px solid #000"}}>ΦΟΡΟΣ
                                                ΕΚΡΟΩΝ<br/>που
                                                αναλογεί
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td rowSpan={8} style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000"
                                            }}></td>
                                            <td rowSpan={4} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.6rem"
                                            }}>
                                                I. ΕΚΡΟΕΣ,<br/>ΕΝΔΟΚ. ΑΠΟΚΤΗΣΕΙΣ<br/>& ΠΡΑΞΕΙΣ ΛΗΠΤΗ<br/>σε
                                                λοιπή Ελλάδα
                                                εκτός<br/>από τα νησιά Αιγαίου.
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>301
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(301)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>13
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>331
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(331)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>302
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(302)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>6
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>332
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(332)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>303
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(303)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>24
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>333
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(333)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>308
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(308)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>4
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>338
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(338)} /></td>
                                        </tr>
                                        <tr>
                                            <td rowSpan={4} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.6rem"
                                            }}>
                                                II. ΕΚΡΟΕΣ,<br/>ΕΝΔΟΚ. ΑΠΟΚΤΗΣΕΙΣ<br/>& ΠΡΑΞΕΙΣ ΛΗΠΤΗ<br/>στα
                                                νησιά
                                                Αιγαίου<br/>και από λοιπή Ελλάδα<br/>προς τα νησιά αυτά
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>304
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(304)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>9
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>334
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(334)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>305
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(305)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>4
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>335
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(335)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>306
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(306)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>17
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>336
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(336)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>309
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(309)} /></td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>3
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>339
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(339)} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2}
                                                className="align-middle p-1 text-center font-weight-bold"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    fontSize: "0.65rem"
                                                }}>
                                                ΣΥΝΟΛΟ<br/>ΦΟΡΟΛ/ΤΕΩΝ ΕΚΡΟΩΝ
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>307
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(307)} /></td>
                                            <td className="align-middle p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.65rem"
                                            }}>ΣΥΝ.<br/>ΦΟΡ.
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>337
                                            </td>
                                            <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                <SingleCell value={getFieldValue(337)} /></td>
                                        </tr>
                                        {[
                                            {code: 342, label: "Ενδοκοινοτικές παραδόσεις"},
                                            {code: 345, label: "Ενδοκ/τικές παροχές υπηρεσιών άρθρ.14.2.α"},
                                            {code: 348, label: "Εξαγωγές & απαλλαγές πλοίων & αεροσκαφών"},
                                            {
                                                code: 349,
                                                label: "Λοιπές εκροές χωρίς ΦΠΑ με δικαίωμα έκπτωσης"
                                            },
                                            {
                                                code: 310,
                                                label: "Εκροές απαλλ/νες & εξαιρ/νες χωρίς δικαίωμα έκπτωσης"
                                            },
                                            {code: 311, label: "ΣΥΝΟΛΟ ΕΚΡΟΩΝ"},
                                            {
                                                code: 314,
                                                label: "Ενδοκοινοτικές Αποκτήσεις χωρίς δικαίωμα έκπτωσης που δεν συμμετέχουν στον ΚΥΚΛΟ ΕΡΓΑΣΙΩΝ ΦΠΑ"
                                            },
                                            {
                                                code: 315,
                                                label: "Ενδοκοινοτικές Λήψεις υπηρεσιών χωρίς δικαίωμα έκπτ. που δεν συμμετέχουν στον ΚΥΚΛΟ ΕΡΓΑΣΙΩΝ ΦΠΑ"
                                            },
                                            {
                                                code: 313,
                                                label: "Λοιπές πράξεις που δεν συμμετέχουν στον ΚΥΚΛΟ ΕΡΓΑΣΙΩΝ ΦΠΑ"
                                            },
                                            {code: 312, label: "Κύκλος εργασιών ΦΠΑ"}
                                        ].map((item, index) => (
                                            <tr key={item.code}>
                                                <td colSpan={2} className="align-middle text-center p-1"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: index === 9 ? "none" : "1px solid #000",
                                                        fontSize: "0.6rem"
                                                    }}>
                                                    {item.label}
                                                </td>
                                                <td className="align-middle font-weight-bold p-0" style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: index === 9 ? "none" : "1px solid #000", ...grayBg,
                                                    fontSize: "0.7rem"
                                                }}>
                                                    {item.code}
                                                </td>
                                                <td className="p-0" style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: index === 9 ? "none" : "1px solid #000"
                                                }}>
                                                    <SingleCell value={getFieldValue(item.code)} />
                                                </td>
                                                {index === 0 && (
                                                    <td colSpan={3} rowSpan={10}
                                                        style={{border: "none"}}></td>
                                                )}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </Col>

                                <Col lg={6} className="p-0">
                                    <table className="w-100 text-center m-0"
                                           style={{tableLayout: "fixed", borderCollapse: "collapse"}}>
                                        <colgroup>
                                            <col style={{width: "4%"}}/>
                                            <col style={{width: "30%"}}/>
                                            <col style={{width: "8%"}}/>
                                            <col style={{width: "25%"}}/>
                                            <col style={{width: "8%"}}/>
                                            <col style={{width: "25%"}}/>
                                        </colgroup>
                                        <thead>
                                        <tr className="align-middle">
                                            <th rowSpan={2} className="font-weight-bold" style={{
                                                borderRight: "1px solid #000",
                                                padding: "9.7px",
                                                borderBottom: "1px solid #000"
                                            }}>β
                                            </th>
                                            <th colSpan={3} style={{
                                                fontSize: "0.65rem",
                                                borderRight: "1px solid #000",
                                                padding: "9.7px",
                                                borderBottom: "1px solid #000"
                                            }}>ΕΙΣΡΟΕΣ με δικαίωμα έκπτωσης
                                            </th>
                                            <th colSpan={2} style={{
                                                fontSize: "0.65rem",
                                                padding: "9.7px",
                                                borderBottom: "1px solid #000"
                                            }}>ΦΟΡΟΣ ΕΙΣΡΟΩΝ
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {[
                                            {
                                                c1: 361,
                                                c2: 381,
                                                label: "Αγορές & δαπάνες στο εσωτερικό της χώρας"
                                            },
                                            {
                                                c1: 362,
                                                c2: 382,
                                                label: "Αγορές & εισαγωγές επενδ. αγαθών (πάγια)"
                                            },
                                            {
                                                c1: 363,
                                                c2: 383,
                                                label: "Λοιπές εισαγωγές εκτός επενδ.αγαθών (πάγια)"
                                            },
                                            {c1: 364, c2: 384, label: "Ενδοκοινοτικές αποκτήσεις αγαθών"},
                                            {
                                                c1: 365,
                                                c2: 385,
                                                label: "Ενδοκοινοτικές λήψεις υπηρεσιών άρθρ.14.2.α"
                                            },
                                            {c1: 366, c2: 386, label: "Λοιπές πράξεις λήπτη"},
                                            {c1: 367, c2: 387, label: "ΣΥΝΟΛΟ ΦΟΡΟΛ/ΤΕΩΝ ΕΙΣΡΟΩΝ"},
                                        ].map(item => (
                                            <tr key={item.c1}>
                                                <td colSpan={2} className="align-middle p-1 text-center"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000",
                                                        fontSize: "0.6rem"
                                                    }}>
                                                    {item.label}
                                                </td>
                                                <td className="align-middle font-weight-bold p-0" style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000", ...grayBg,
                                                    fontSize: "0.7rem"
                                                }}>{item.c1}</td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <SingleCell value={getFieldValue(item.c1)} />
                                                </td>
                                                <td className="align-middle font-weight-bold p-0" style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000", ...grayBg,
                                                    fontSize: "0.7rem"
                                                }}>{item.c2}</td>
                                                <td className="p-0"
                                                    style={{borderBottom: "1px solid #000"}}>
                                                    <SingleCell value={getFieldValue(item.c2)} />
                                                </td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className="align-middle font-weight-bold" style={{
                                                padding: "6.5px",
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>δ
                                            </td>
                                            <td colSpan={3} className="align-middle font-weight-bold p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    fontSize: "0.6rem"
                                                }}>
                                                <div
                                                    className="d-flex flex-column h-100 w-100 justify-content-center text-center"
                                                    style={{padding: "6.5px"}}>
                                                    ΠΡΟΣΤΙΘΕΜΕΝΑ ΠΟΣΑ ΣΤΟ<br/>ΣΥΝΟΛΟ ΤΟΥ ΦΟΡΟΥ ΕΙΣΡΟΩΝ
                                                </div>
                                            </td>
                                            <td colSpan={2} rowSpan={3} className="align-middle p-0"
                                                style={{borderBottom: "none"}}>
                                                <div
                                                    className="d-flex h-100 w-100 align-items-stretch pl-1 pr-2 py-1">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "25px",
                                                            flexShrink: 0,
                                                            marginRight: "4px"
                                                        }}>
                                                        <div style={{
                                                            marginTop: "40px",
                                                            backgroundColor: "#000",
                                                            color: "#fff",
                                                            writingMode: "vertical-rl",
                                                            transform: "rotate(180deg)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "0.4rem",
                                                            fontWeight: "bold",
                                                            width: "100%",
                                                            height: "60px",
                                                            clipPath: "polygon(100% 0%, 100% 100%, 0% 50%)"
                                                        }}>ΣΥΝΟΛΟ
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="flex-fill d-flex flex-column align-items-center justify-content-center w-100">
                                                        <div style={{
                                                            width: "100%",
                                                            height: "12px",
                                                            backgroundColor: "#000",
                                                            clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)"
                                                        }}></div>
                                                        <div className="font-weight-bold" style={{
                                                            fontSize: "1.2rem",
                                                            lineHeight: "1",
                                                            margin: "2px 0"
                                                        }}>+
                                                        </div>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "12px",
                                                            backgroundColor: "#000",
                                                            clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
                                                            marginBottom: "3px"
                                                        }}></div>
                                                        <div className="d-flex w-100">
                                                            <div style={{width: "35px"}}></div>
                                                        </div>
                                                        <div className="d-flex w-100">
                                                            <div
                                                                className="d-flex align-items-center justify-content-center font-weight-bold"
                                                                style={{
                                                                    width: "35px", ...grayBg,
                                                                    border: "1px solid #000"
                                                                }}>410
                                                            </div>
                                                            <div className="flex-fill"
                                                                 style={{
                                                                     border: "1px solid #000",
                                                                     borderLeft: "none"
                                                                 }}>
                                                                <SingleCell value={getFieldValue(410)} /></div>
                                                        </div>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "12px",
                                                            backgroundColor: "#000",
                                                            clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)",
                                                            marginTop: "3px"
                                                        }}></div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.6rem"
                                            }}>Λοιπά προστιθέμ. ποσά
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>402
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(402)} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.6rem"
                                            }}>Ποσά διακανονισμών προς<br/>έκπτωση προηγούμενης<br/>διαχειριστικής
                                                περιόδου
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>407
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(407)} /></td>
                                        </tr>
                                        <tr>
                                            <td className="align-middle font-weight-bold p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.7rem"
                                            }}>ε
                                            </td>
                                            <td colSpan={3}
                                                className="align-middle text-center font-weight-bold p-1"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    fontSize: "0.6rem"
                                                }}>
                                                ΑΦΑΙΡΟΥΜΕΝΑ ΠΟΣΑ ΑΠΟ ΤΟ<br/>ΣΥΝΟΛΟ ΤΟΥ ΦΟΡΟΥ ΕΙΣΡΟΩΝ
                                            </td>
                                            <td colSpan={2} rowSpan={4} className="align-middle p-0"
                                                style={{borderBottom: "none"}}>
                                                <div
                                                    className="d-flex h-100 w-100 align-items-stretch pl-1 pr-2 py-1">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "25px",
                                                            flexShrink: 0,
                                                            marginRight: "4px"
                                                        }}>
                                                        <div style={{
                                                            marginTop: "30px",
                                                            backgroundColor: "#000",
                                                            color: "#fff",
                                                            writingMode: "vertical-rl",
                                                            transform: "rotate(180deg)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "0.5rem",
                                                            fontWeight: "bold",
                                                            width: "100%",
                                                            height: "100px",
                                                            clipPath: "polygon(100% 0%, 100% 100%, 0% 50%)"
                                                        }}>ΣΥΝΟΛΟ
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="flex-fill d-flex flex-column align-items-center justify-content-center w-100">
                                                        <div className="font-weight-bold mt-4" style={{
                                                            fontSize: "1.6rem",
                                                            lineHeight: "1",
                                                            margin: "2px 0"
                                                        }}>-
                                                        </div>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "12px",
                                                            backgroundColor: "#000",
                                                            clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
                                                            marginBottom: "3px"
                                                        }}></div>
                                                        <div className="d-flex w-100">
                                                            <div style={{width: "35px"}}></div>
                                                        </div>
                                                        <div className="d-flex w-100">
                                                            <div
                                                                className="d-flex align-items-center justify-content-center font-weight-bold"
                                                                style={{
                                                                    width: "35px", ...grayBg,
                                                                    border: "1px solid #000"
                                                                }}>428
                                                            </div>
                                                            <div className="flex-fill"
                                                                 style={{
                                                                     border: "1px solid #000",
                                                                     borderLeft: "none"
                                                                 }}>
                                                                <SingleCell value={getFieldValue(428)} /></div>
                                                        </div>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "12px",
                                                            backgroundColor: "#000",
                                                            clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)",
                                                            marginTop: "3px"
                                                        }}></div>
                                                        <div className="font-weight-bold" style={{
                                                            fontSize: "1.4rem",
                                                            lineHeight: "1",
                                                            margin: "2px 0"
                                                        }}>=
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.6rem"
                                            }}>ΦΠΑ εισροών που πρέπει<br/>να μειωθεί βάσει ProRata
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>411
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(411)} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000",
                                                fontSize: "0.6rem"
                                            }}>Λοιπά<br/>αφαιρούμενα ποσά
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "1px solid #000", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>422
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>
                                                <SingleCell value={getFieldValue(422)} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} className="align-middle p-1" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "none",
                                                fontSize: "0.6rem"
                                            }}>Ποσά διακανονισμών προς<br/>καταβολή προηγούμενης<br/>διαχειριστικής
                                                περιόδου
                                            </td>
                                            <td className="align-middle font-weight-bold p-0" style={{
                                                borderRight: "1px solid #000",
                                                borderBottom: "none", ...grayBg,
                                                fontSize: "0.7rem"
                                            }}>423
                                            </td>
                                            <td className="p-0"
                                                style={{
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "none"
                                                }}>
                                                <SingleCell value={getFieldValue(423)} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={4} style={{borderTop: borderDark}}></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} style={{borderBottom: "none"}}></td>
                                            <td colSpan={2}
                                                className="align-middle text-right pr-2 font-weight-bold"
                                                style={{
                                                    border: "2px solid #000",
                                                    fontSize: "0.6rem",
                                                    borderRight: "none"
                                                }}>ΥΠΟΛΟΙΠΟ ΦΟΡΟΥ ΕΙΣΡΟΩΝ
                                            </td>
                                            <td className="align-middle font-weight-bold p-0 text-center"
                                                style={{
                                                    border: "2px solid #000", ...grayBg,
                                                    fontSize: "0.7rem"
                                                }}>430
                                            </td>
                                            <td className="p-0" style={{border: "2px solid #000"}}>
                                                <SingleCell value={getFieldValue(430)} /></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </Col>
                            </Row>
                        </div>

                        <div style={{borderBottom: borderDark}}>
                            <div className="p-1 d-flex align-items-center bg-white"
                                 style={{borderBottom: borderDark, fontSize: "0.75rem", color: "#000"}}>
                                <span className="font-weight-bold mr-2">Γ. ΠΙΝΑΚΑΣ ΕΚΚΑΘΑΡΙΣΗΣ ΤΟΥ ΦΟΡΟΥ</span>
                                <span className="font-weight-normal">για καταβολή, έκπτωση ή επιστροφή (κωδ. 337 μείον κωδ. 430)</span>
                            </div>

                            <table className="w-100 m-0 bg-white" style={{
                                tableLayout: "fixed",
                                borderCollapse: "collapse",
                                borderBottom: "1px solid #000"
                            }}>
                                <colgroup>
                                    <col style={{width: "16%"}}/>
                                    <col style={{width: "5%"}}/>
                                    <col style={{width: "18%"}}/>
                                    <col style={{width: "16%"}}/>
                                    <col style={{width: "5%"}}/>
                                    <col style={{width: "18%"}}/>
                                    <col style={{width: "22%"}}/>
                                </colgroup>
                                <tbody>
                                <tr>
                                    <td className="align-middle text-center font-weight-bold p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>ΠΙΣΤΩΤΙΚΟ<br/>ΥΠΟΛΟΙΠΟ
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>470
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(470)} /></td>
                                    <td className="align-middle text-center font-weight-bold p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>ΧΡΕΩΣΤΙΚΟ<br/>ΥΠΟΛΟΙΠΟ
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>480
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(480)} /></td>
                                    <td rowSpan={8} className="align-top p-2" style={{
                                        borderBottom: "none",
                                        borderTop: "none",
                                        backgroundColor: "#fff"
                                    }}>
                                        <span className="font-weight-bold d-inline-block mb-1"
                                              style={{fontSize: "0.7rem"}}>
                                            Σημειώσεις:
                                        </span>
                                        <div style={{
                                            fontSize: "0.75rem",
                                            lineHeight: "1.2rem",
                                            minHeight: "150px",
                                            whiteSpace: "pre-wrap"
                                        }}>
                                            {fields['comments'] || fields['notes'] || ""}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={6} className="align-middle px-2 py-1 font-weight-normal"
                                        style={{
                                            fontSize: "0.75rem",
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000",
                                            letterSpacing: "0.5px"
                                        }}>
                                        Προσδιορισμός οριστικού ποσού προς καταβολή, έκπτωση ή επιστροφή
                                    </td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>Πιστωτικό υπόλοιπο<br/>προηγ. φορολ. περιόδου
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>401
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(401)} />
                                    </td>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>Χρεωστικό μέχρι 30€<br/>προηγ. φορολ. περιόδου
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>483
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(483)} />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>Βεβαιωμένο ποσό κωδ.511 προηγ.<br/>δηλώσεων της ίδιας φορ. περιόδου
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>403
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(403)} />
                                    </td>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>ΠΟΣΟ που σας επιστράφηκε<br/>ή ζητήσατε την επιστροφή του
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>505
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(505)} />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>Φόρος που έχει δεσμευτεί<br/>μέσω τραπεζών
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>404
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(404)} />
                                    </td>
                                    <td colSpan={3} className="p-0"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}></td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center font-weight-bold p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>ΠΟΣΟ<br/>για έκπτωση
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>502
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(502)} />
                                    </td>
                                    <td className="align-middle text-center font-weight-bold p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>ΠΟΣΟ**<br/>προς καταβολή
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>511
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(511)} />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center font-weight-bold p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>ΑΙΤΟΥΜΕΝΟ ΠΟΣΟ<br/>για επιστροφή
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>503
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(503)} />
                                    </td>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        lineHeight: "1"
                                    }}>Καταβολή ποσού<br/>κωδ. 511 σε 2 δόσεις
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>523
                                    </td>
                                    <td className="p-0 align-middle"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <div
                                            className="d-flex w-100 h-100 font-weight-bold text-center align-items-stretch"
                                            style={{fontSize: "0.65rem", minHeight: "35px"}}>
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{width: "35%", borderRight: "1px solid #000"}}>
                                                ΝΑΙ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "15%",
                                                    borderRight: "1px solid #000"
                                                }}>
                                                <CrossableNumber number={1}
                                                                 isCrossed={Number(fields['523']) === 1}/>
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{width: "35%", borderRight: "1px solid #000"}}>
                                                ΟΧΙ
                                            </div>
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{width: "15%"}}>
                                                <CrossableNumber number={2}
                                                                 isCrossed={Number(fields['523']) === 2 || Number(fields['523']) === 0}/>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="align-middle p-1 text-center font-weight-bold" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000",
                                        letterSpacing: "1px"
                                    }}>ΑΙΤΙΑ ΕΠΙΣΤΡΟΦΗΣ
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>507
                                    </td>
                                    <td colSpan={4} className="align-middle p-0"
                                        style={{
                                            borderRight: "2px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <div className="d-flex w-100 h-100 align-items-stretch"
                                             style={{
                                                 fontSize: "0.6rem",
                                                 minHeight: "30px",
                                                 lineHeight: "1"
                                             }}>
                                            {[
                                                { label: "ΑΠΑΛΛ. ΠΡΑΞΕΙΣ", num: 1 },
                                                { label: "ΠΑΓΙΑ", num: 2 },
                                                { label: "ΑΝΑΣΤ. ΚΑΤΑΒ.", num: 3 },
                                                { label: "ΔΙΑΦΟΡΑ ΣΥΝΤ.", num: 4 },
                                                { label: "ΑΛΛΗ ΑΙΤΙΑ", num: 5 },
                                                { label: "ΜΕΙΩΣΗ ΧΡΕΩΣΤ. ΥΠΟΛ.", num: 6 }
                                            ].map((reason, idx) => (
                                                <div
                                                    key={reason.num}
                                                    className={`d-flex flex-fill align-items-center ${idx < 5 ? "border-right border-dark" : ""}`}
                                                >
                                                    <span className="flex-fill text-center px-1" style={{ fontSize: "0.55rem" }}>
                                                        {reason.label}
                                                    </span>
                                                    <span
                                                        className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                        style={{ fontSize: "0.6rem" }}>
                                                        <CrossableNumber number={reason.num} isCrossed={is507Crossed(reason.num)} />
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "none",
                                        lineHeight: "1"
                                    }}>ΑΙΤΗΣΗ ΣΥΜΨΗΦΙΣΜΟΥ του αιτούμενου προς<br/>επιστροφή ποσού με
                                        μελλοντικές
                                        οφειλές
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "none"
                                    }}>508
                                    </td>
                                    <td colSpan={4} className="p-0"
                                        style={{borderRight: "2px solid #000", borderBottom: "none"}}></td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="p-2 border-bottom border-dark bg-white">
                            <div className="d-flex align-items-center">
                                <div className="custom-control custom-checkbox mb-0 mr-2">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="chkNoIbanView"
                                        checked={Boolean(fields['509'] === 1 || fields['f509'] === 1 || fields['noIbanAccount'])}
                                        disabled={true}
                                    />
                                    <label
                                        className="custom-control-label"
                                        htmlFor="chkNoIbanView"
                                    ></label>
                                </div>
                                <label
                                    htmlFor="chkNoIbanView"
                                    className="mb-0 font-weight-normal"
                                    style={{fontSize: "0.75rem", userSelect: "none"}}
                                >
                                    Δεν τηρώ λογαριασμό πληρωμών (ΙΒΑΝ) σε πάροχο υπηρεσιών πληρωμών στην
                                    Ελλάδα και σε χώρα εντός του Ενιαίου Χώρου Πληρωμών σε ευρώ (SEPA)
                                </label>
                            </div>
                        </div>

                        <div style={{borderBottom: borderDark}}>
                            <table className="w-100 m-0 bg-white" style={{
                                tableLayout: "fixed",
                                borderCollapse: "collapse",
                                borderBottom: "1px solid #000"
                            }}>
                                <colgroup>
                                    <col style={{width: "15%"}}/>
                                    <col style={{width: "18%"}}/>
                                    <col style={{width: "5%"}}/>
                                    <col style={{width: "19%"}}/>
                                    <col style={{width: "18%"}}/>
                                    <col style={{width: "5%"}}/>
                                    <col style={{width: "20%"}}/>
                                </colgroup>
                                <tbody>
                                <tr style={{borderBottom: "1px solid #000"}}>
                                    <td rowSpan={3}
                                        className="align-middle text-center font-weight-bold p-1"
                                        style={{
                                            fontSize: "0.7rem",
                                            borderRight: "1px solid #000",
                                            letterSpacing: "1px",
                                            backgroundColor: "#fff"
                                        }}>
                                        ΠΛΗΡΟΦΟΡΙΑΚΑ<br/>ΣΤΟΙΧΕΙΑ
                                    </td>
                                    <td colSpan={3} style={{borderRight: "1px solid #000"}}></td>
                                    <td colSpan={3}></td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>Πωλήσεις α' κατοικίας
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>906
                                    </td>
                                    <td className="p-0"
                                        style={{
                                            borderRight: "1px solid #000",
                                            borderBottom: "1px solid #000"
                                        }}>
                                        <SingleCell value={getFieldValue(906)} /></td>
                                    <td className="align-middle text-center p-1" style={{
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>Αξία εισροών κωδ.906
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0" style={{
                                        ...grayBg,
                                        fontSize: "0.6rem",
                                        borderRight: "1px solid #000",
                                        borderBottom: "1px solid #000"
                                    }}>907
                                    </td>
                                    <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                        <SingleCell value={getFieldValue(907)} /></td>
                                </tr>
                                <tr>
                                    <td className="align-middle text-center p-1"
                                        style={{
                                            fontSize: "0.6rem",
                                            borderRight: "1px solid #000",
                                            lineHeight: "1"
                                        }}>Μικτό
                                        κέρδος επιχ.<br/>άρθρου 50
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0"
                                        style={{
                                            ...grayBg,
                                            fontSize: "0.6rem",
                                            borderRight: "1px solid #000"
                                        }}>908
                                    </td>
                                    <td className="p-0" style={{borderRight: "1px solid #000"}}>
                                        <SingleCell value={getFieldValue(908)} /></td>
                                    <td className="align-middle text-center p-1"
                                        style={{
                                            fontSize: "0.6rem",
                                            borderRight: "1px solid #000",
                                            lineHeight: "1"
                                        }}>Αξία
                                        απαλλασσόμενων<br/>πράξεων από και προς την<br/>περιοχή του Αγίου
                                        Όρους
                                    </td>
                                    <td className="font-weight-bold text-center align-middle p-0"
                                        style={{
                                            ...grayBg,
                                            fontSize: "0.6rem",
                                            borderRight: "1px solid #000"
                                        }}>912
                                    </td>
                                    <td className="p-0"><SingleCell value={getFieldValue(912)} /></td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-white">
                            <div className="p-2 pt-2 d-flex justify-content-between align-items-end"
                                 style={{fontSize: "0.6rem"}}>
                                <div>
                                    * Συμπληρώνεται από την Υπηρεσία.<br/>
                                    - Αν οποιοδήποτε ποσό που καταχωρείται είναι αρνητικός αριθμός,
                                    σημειώστε το
                                    πρόσημο
                                    πλήν (-) πριν απ' αυτό.<br/>
                                    - Τα ποσά σε ΕΥΡΩ αναγράφονται <strong>υποχρεωτικά</strong> με τη χρήση
                                    δύο
                                    (2)
                                    δεκαδικών ψηφίων μετά την υποδιαστολή, π.χ. 45,00 ή 45,63.<br/>
                                    ** Χρεωστικό υπόλοιπο μέχρι τριάντα (30) ευρώ μεταφέρεται για καταβολή
                                    στην
                                    επόμενη
                                    φορολογική περίοδο, με εξαίρεση την παύση εργασιών φορολογητέας
                                    δραστηριότητας.
                                </div>
                                <div className="text-right font-weight-bold"
                                     style={{whiteSpace: "nowrap", fontSize: "0.6rem"}}>
                                    ΕΚΔΟΣΗ 2025 &nbsp;&nbsp;&nbsp;&nbsp; 050 - Φ.Π.Α.<br/>
                                    ΑΑΔΕ<br/>
                                    Γ.Γ.Π.Σ.Ψ.Δ. - Δ.Λ.Ο.Σ.Ε.Λ.Υ.Υ.Χ.
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div
                className="w-100 position-sticky bg-white border-top py-1 px-3 d-flex justify-content-start align-items-center mt-auto print-hide"
                style={{
                    bottom: 0,
                    zIndex: 3,
                    boxShadow: "0 -4px 10px -2px rgba(0,0,0,0.1)"
                }}
            >
                <span className="mr-2 text-dark font-weight-bold" style={{fontSize: "0.7rem", fontWeight: "600"}}>
                    Zoom:
                </span>
                <Button
                    variant="light"
                    size="sm"
                    className="py-1 px-2 border font-weight-bold"
                    onClick={() => setZoomLevel(Math.max(zoomLevel - 0.1, 0.4))}
                >
                    -
                </Button>
                <span className="mx-2 font-weight-bold text-primary"
                      style={{minWidth: "35px", textAlign: "center", fontSize: "0.75rem"}}>
                    {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                    variant="light"
                    size="sm"
                    className="py-1 px-2 border font-weight-bold"
                    onClick={() => setZoomLevel(Math.min(zoomLevel + 0.1, 2.0))}
                >
                    +
                </Button>
                {zoomLevel !== 1 && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-1 px-1 ml-2 border"
                        onClick={() => setZoomLevel(1)}
                        title="Επαναφορά στο 100%"
                    >
                        <i className="fa-solid fa-arrow-rotate-left"></i>
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AccountingVatDeclarationView;